import { makeAutoObservable, runInAction } from 'mobx';
import React, { useEffect } from 'react';

import { apiFetch, ApiRequest, ApiResponse } from 'framework/api';
import { MobxManager } from 'framework/mobx-integration';
import { RemoteEventsManager } from 'core/remoteEventsManager';
import { EventType } from 'framework/entities/events';

export type DataHolderConfig<TData, TResponseData = TData> = {
	request?: ApiRequest<TResponseData> | (() => ApiRequest<TResponseData>);
	converter?: (data: TResponseData) => TData;
	onLoaded?: (dataHolder: DataHolder<TData, TResponseData>) => void;
	initialValue?: TData;
	subscriptions?: { eventType: EventType }[];
	resetDataBeforeLoading?: boolean;
};

export class DataHolder<TData, TResponseData = TData> {
	loading: boolean;
	loaded: boolean;
	error: boolean;
	message: string;

	data: TData;

	mobx = new MobxManager();
	subscription: { unsubscribe: () => void };
	response: ApiResponse<TResponseData>;

	constructor(public config?: DataHolderConfig<TData, TResponseData>) {
		this.config ??= {};
		this.data = this.config.initialValue;

		makeAutoObservable(this);

		this.mobx.reaction(
			() => this.getRequest(),
			(request) => {
				void this.loadData(request);
			},
			{
				fireImmediately: true,
			},
		);

		this.mobx.reaction(
			() => this.config.subscriptions,
			() => {
				this.subscription?.unsubscribe();

				if (this.config.subscriptions?.length > 0) {
					this.subscription = RemoteEventsManager.subscribeCallback(this.config.subscriptions, this.reload);
				}
			},
		);
	}

	getRequest = () => {
		return typeof this.config.request == 'function' ? this.config.request() : this.config.request;
	};

	loadData = async (request: ApiRequest<TResponseData>) => {
		if (this.config.resetDataBeforeLoading) {
			this.data = this.config.initialValue;
		}

		this.loaded = false;
		this.error = false;
		this.message = '';

		if (request == null) {
			this.config.onLoaded?.(this);
			return;
		}

		this.loading = true;

		this.response = await apiFetch(request);

		runInAction(() => {
			this.loading = false;
			if (!this.response.success) {
				this.error = true;
				this.message = this.response.message;
				this.data = this.config.initialValue;
			} else {
				this.loaded = true;
				this.data = this.config.converter
					? this.config.converter(this.response.data)
					: (this.response.data as unknown as TData);
			}

			this.config.onLoaded?.(this);
		});
	};

	reload = () => {
		return this.loadData(this.getRequest());
	};

	destroy() {
		this.mobx.destroy();
		this.subscription?.unsubscribe();
	}
}

export function useDataHolder<TData>(config: DataHolderConfig<TData>) {
	const [dataHolder] = React.useState(
		() =>
			new DataHolder<TData>({
				initialValue: config.initialValue,
				onLoaded: config.onLoaded,
				request: config.request,
				converter: config.converter,
			}),
	);

	useEffect(() => {
		Object.assign(dataHolder.config, config);
	}, [config.request, config.converter, config.onLoaded]);

	return dataHolder;
}
